/**
 * chatLayoutStore — adaptive chat ↔ workspace layout.
 *
 * Three modes inspired by Linear Insights / Notion AI / Hex / Cursor:
 *
 *   - ``split``      : chat 38% / workspace 62%  (default for chat-led tabs)
 *   - ``canvas-led`` : chat collapsed to 48px rail / workspace ~full-bleed
 *                       (default for Dashboard, Canvas — surfaces where the
 *                       canvas IS the primary task)
 *   - ``focus``      : chat fullscreen (760px max centred) / workspace hidden
 *                       (reading mode; ``Cmd+\\`` toggle)
 *
 * Resolution order for the active mode on a given tab:
 *   1. user explicit per-tab override (``perTab[tab]``)
 *   2. tab default (``DEFAULT_BY_TAB`` — canvas-led for Dashboard/Canvas, split everywhere else)
 *   3. global fallback (``globalMode``)
 *
 * The store is GLOBAL + persisted. Per-tab overrides survive reloads so a
 * user who collapsed Dashboard chat won't have it re-expand on every tab
 * switch. Reference patterns: Linear Cmd+. focus toggle, Notion Cmd+\
 * side-panel collapse, Granola wide-chat in transcript view.
 *
 * The chrome stays driven by Tailwind classes via a ``data-chat-mode``
 * attribute on the outer container so Tailwind picks up the variants
 * statically at build time (no runtime style injection).
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { WorkspaceTab } from "@/features/workspace-router/state/workspaceStore";

export type ChatLayoutMode = "split" | "canvas-led" | "focus";

/** Tabs where the canvas (not the chat) is the primary surface. */
const CANVAS_LED_TABS = new Set<WorkspaceTab>(["canvases", "dashboards"]);

/**
 * Tab default mode — used when the user has no explicit per-tab override.
 * Returns ``canvas-led`` for surfaces where the canvas is primary, else
 * the user's global mode (so ``Cmd+\\`` still works as a global toggle on
 * chat-led tabs).
 */
function defaultForTab(tab: WorkspaceTab, globalMode: ChatLayoutMode): ChatLayoutMode {
  if (CANVAS_LED_TABS.has(tab)) return "canvas-led";
  // canvas-led isn't a meaningful global fallback (it's surface-specific),
  // so collapse to split if globalMode somehow ended up there.
  return globalMode === "canvas-led" ? "split" : globalMode;
}

/** Full cycle rotation. The per-tab cycle is filtered so chat-led tabs
 *  skip ``canvas-led`` (it would collapse to ``split`` via ``defaultForTab``
 *  and Cmd+. would feel like a no-op step). */
const CYCLE_ORDER: ChatLayoutMode[] = ["split", "canvas-led", "focus"];

function cycleOrderForTab(tab: WorkspaceTab): ChatLayoutMode[] {
  return CANVAS_LED_TABS.has(tab)
    ? CYCLE_ORDER
    : CYCLE_ORDER.filter((m) => m !== "canvas-led");
}

/** Default chat panel width in ``split`` mode, expressed as percentage. */
export const DEFAULT_SPLIT_RATIO = 38;

/** Min/max chat ratio in ``split`` mode. Symmetric around 50%: the chat
 *  never drops below 38% (the default — the workspace-led extreme) nor
 *  goes above 62% (the chat-led extreme). To go beyond either side the
 *  user switches to ``canvas-led`` (chat collapsed) or ``focus`` (chat
 *  full-screen) via the LayoutSwitcher. */
export const MIN_SPLIT_RATIO = 38;
export const MAX_SPLIT_RATIO = 62;

/** Snap points (percent) — drag releases that land within ±SNAP_TOLERANCE
 *  of one of these values jump to the snap. Pattern Cursor / Figma. */
export const SPLIT_SNAP_POINTS: readonly number[] = [38, 50, 62];
export const SPLIT_SNAP_TOLERANCE = 2.5;

/** Re-clamp a possibly-stale persisted ratio to the current bounds.
 *  Old localStorage values from before the 38/62 bounds (when min was 20
 *  and max was 70) get coerced into range on read. */
export function clampSplitRatio(raw: number | undefined): number {
  if (typeof raw !== "number" || !Number.isFinite(raw)) {
    return DEFAULT_SPLIT_RATIO;
  }
  return Math.max(MIN_SPLIT_RATIO, Math.min(MAX_SPLIT_RATIO, raw));
}

interface ChatLayoutState {
  /** User explicit per-tab mode override. Cleared via ``resetForTab``. */
  perTab: Partial<Record<WorkspaceTab, ChatLayoutMode>>;
  /** Per-tab chat width % in ``split`` mode. Persisted so each tab
   *  remembers its preferred ratio (Linear/Cursor split-editor pattern). */
  splitRatioByTab: Partial<Record<WorkspaceTab, number>>;
  /** Global fallback for tabs without per-tab override and no tab default. */
  globalMode: ChatLayoutMode;

  setModeForTab: (tab: WorkspaceTab, mode: ChatLayoutMode) => void;
  resetForTab: (tab: WorkspaceTab) => void;
  /** Cycle through the 3 modes for the given tab. Driven by Cmd+\\. */
  cycleForTab: (tab: WorkspaceTab) => void;
  /** Toggle focus on/off for the given tab (preserves canvas-led/split when leaving focus). */
  toggleFocusForTab: (tab: WorkspaceTab) => void;
  /** Set the per-tab split ratio (chat width %). Clamped to [MIN, MAX] and
   *  snapped to ``SPLIT_SNAP_POINTS`` when close. */
  setSplitRatio: (tab: WorkspaceTab, ratio: number) => void;
  /** Reset the per-tab split ratio back to DEFAULT_SPLIT_RATIO (38%). */
  resetSplitRatio: (tab: WorkspaceTab) => void;
  /** Legacy global setter — kept for callers without tab context. */
  setGlobalMode: (mode: ChatLayoutMode) => void;
}

function clampAndSnap(raw: number): number {
  const clamped = Math.max(MIN_SPLIT_RATIO, Math.min(MAX_SPLIT_RATIO, raw));
  for (const snap of SPLIT_SNAP_POINTS) {
    if (Math.abs(clamped - snap) <= SPLIT_SNAP_TOLERANCE) return snap;
  }
  return Math.round(clamped * 10) / 10; // 1 decimal place
}

export const useChatLayoutStore = create<ChatLayoutState>()(
  persist(
    (set, get) => ({
      perTab: {},
      splitRatioByTab: {},
      globalMode: "split",
      setModeForTab: (tab, mode) =>
        set((s) => ({ perTab: { ...s.perTab, [tab]: mode } })),
      setSplitRatio: (tab, ratio) =>
        set((s) => ({
          splitRatioByTab: { ...s.splitRatioByTab, [tab]: clampAndSnap(ratio) },
        })),
      resetSplitRatio: (tab) =>
        set((s) => {
          const next = { ...s.splitRatioByTab };
          delete next[tab];
          return { splitRatioByTab: next };
        }),
      resetForTab: (tab) =>
        set((s) => {
          const next = { ...s.perTab };
          delete next[tab];
          return { perTab: next };
        }),
      cycleForTab: (tab) => {
        const current = resolveMode(get(), tab);
        const order = cycleOrderForTab(tab);
        const idx = order.indexOf(current);
        // ``idx === -1`` happens when the current mode isn't in the tab's
        // cycle (e.g. a stale ``canvas-led`` override on a chat-led tab):
        // wrap-around to the first valid step so Cmd+. always feels
        // responsive instead of stuck.
        const next = order[(idx + 1) % order.length] ?? "split";
        set((s) => ({ perTab: { ...s.perTab, [tab]: next } }));
      },
      toggleFocusForTab: (tab) => {
        const current = resolveMode(get(), tab);
        if (current === "focus") {
          // Leaving focus — restore the tab's previous mode if remembered,
          // otherwise fall back to the tab default.
          set((s) => {
            const next = { ...s.perTab };
            const prev = next[tab];
            if (prev === "focus") delete next[tab];
            return { perTab: next };
          });
        } else {
          set((s) => ({ perTab: { ...s.perTab, [tab]: "focus" } }));
        }
      },
      setGlobalMode: (mode) => set({ globalMode: mode }),
    }),
    {
      name: "ando.chat-layout.v2",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        perTab: s.perTab,
        splitRatioByTab: s.splitRatioByTab,
        globalMode: s.globalMode,
      }),
      version: 2,
      // v1 → v2 migration: the v1 schema was a single global ``mode`` field.
      // We migrate it into the new ``globalMode`` slot and start with an
      // empty per-tab override map (so the user immediately sees the SOTA
      // tab defaults: canvas-led for Dashboard/Canvas).
      migrate: (persisted: unknown): { perTab: Record<string, ChatLayoutMode>; globalMode: ChatLayoutMode } => {
        if (
          persisted &&
          typeof persisted === "object" &&
          "mode" in (persisted as Record<string, unknown>)
        ) {
          const legacy = (persisted as { mode?: unknown }).mode;
          const legacyMode: ChatLayoutMode =
            legacy === "focus" || legacy === "canvas-led" || legacy === "split"
              ? legacy
              : "split";
          return { perTab: {}, globalMode: legacyMode };
        }
        return { perTab: {}, globalMode: "split" };
      },
    },
  ),
);

// ---------------------------------------------------------------------------
// Pure resolution + selector helpers
// ---------------------------------------------------------------------------

function resolveMode(state: ChatLayoutState, tab: WorkspaceTab): ChatLayoutMode {
  return state.perTab[tab] ?? defaultForTab(tab, state.globalMode);
}

/**
 * Hook helper — returns the active layout mode for a given tab plus the
 * derived booleans the route shell consumes. Designed as a single selector
 * so the route re-renders at most once per layout-affecting state change.
 */
export function useResolvedChatLayout(tab: WorkspaceTab): {
  readonly mode: ChatLayoutMode;
  readonly isFocus: boolean;
  readonly isCanvasLed: boolean;
  readonly isSplit: boolean;
} {
  const mode = useChatLayoutStore((s) => resolveMode(s, tab));
  return {
    mode,
    isFocus: mode === "focus",
    isCanvasLed: mode === "canvas-led",
    isSplit: mode === "split",
  };
}

// ---------------------------------------------------------------------------
// Legacy selectors — kept for back-compat with callers that don't yet know
// about tab-aware resolution. They fall through to globalMode only, which
// is correct for chat-led tabs and degrades gracefully on canvas-led tabs
// (the new ``useResolvedChatLayout`` hook is the SOTA path).
// ---------------------------------------------------------------------------

export const selectChatLayoutMode = (s: ChatLayoutState): ChatLayoutMode =>
  s.globalMode;
export const selectIsFocusMode = (s: ChatLayoutState): boolean =>
  s.globalMode === "focus";
